import { useState } from 'react';
import { useTranslate } from 'react-polyglot';

import { UserAliasAvatar } from '@genially/ui/build/components/UserAliasAvatar/UserAliasAvatar';

import type { QuestionAnalyticsUserRow } from '../../../../../../utils/buildQuestionAnalytics';
import {
  Badge,
  Detail,
  DetailItem,
  Details,
  Header,
  HeaderButton,
  IconContainer,
  Name,
  StyledCrossIcon,
  StyledDisplayDownIcon,
  StyledSuccessIcon,
  Wrapper,
} from './QuestionUserRow.styled';

interface QuestionUserRowProps {
  row: QuestionAnalyticsUserRow;
}

/**
 * One participant and what they did with this question. The badge says whether they got
 * it right; the chevron, when there is one, says what they actually answered.
 *
 * It never says what the right answer was: the design does not show it, the host has the
 * question on screen, and working it out would need per-subtype logic (a sequence for
 * sorting, a map for blanks) as large as everything else here put together.
 */
export const QuestionUserRow = ({ row }: QuestionUserRowProps) => {
  const t = useTranslate();

  const [isExpanded, setIsExpanded] = useState(false);

  const { username, status, details } = row;

  // No detail, no chevron: a control that opens an empty drawer is worse than no control.
  const isExpandable = details.length > 0;

  const handleClick = () => {
    setIsExpanded(previous => !previous);
  };

  const badge = (() => {
    if (status === 'correct')
      return (
        <IconContainer $type="correct">
          <StyledSuccessIcon />
        </IconContainer>
      );
    if (status === 'incorrect')
      return (
        <IconContainer $type="incorrect">
          <StyledCrossIcon />
        </IconContainer>
      );
    if (status === 'unanswered') {
      return <Badge>{t('__new.play.analyticsResults.question.unanswered')}</Badge>;
    }

    return null;
  })();

  const content = (
    <>
      <UserAliasAvatar name={username} size={UserAliasAvatar.Size.Small} />
      <Name>{username}</Name>
      {badge}
      {isExpandable && <StyledDisplayDownIcon $isExpanded={isExpanded} />}
    </>
  );

  return (
    <Wrapper>
      {isExpandable ? (
        <HeaderButton onClick={handleClick} aria-expanded={isExpanded}>
          {content}
        </HeaderButton>
      ) : (
        <Header>{content}</Header>
      )}

      {isExpandable && isExpanded && (
        <Details>
          {details.map((detail, index) => (
            <Detail
              key={'text' in detail ? `${detail.text}-${index}` : `option-${index}`}
            >
              <DetailItem>
                {'text' in detail
                  ? detail.text
                  : t('__new.play.analyticsResults.option.unnamed', {
                      position: detail.optionPosition,
                    })}
              </DetailItem>
            </Detail>
          ))}
        </Details>
      )}
    </Wrapper>
  );
};
